import { useState, useEffect } from 'react';
import { YouTubeEmbed } from './YouTubeEmbed';
import { BandcampEmbed } from './BandcampEmbed';
import { useArtistData } from '../context/ArtistDataContext';
import { PlatformLinks, type AnyLinks } from './PlatformLinks';
import { ClickableArtistName } from './ClickableArtistName';

interface BandcampEmbedData {
  embedHtml: string;
  albumId?: string;
  trackId?: string;
  title?: string;
  bandcampUrl?: string;
}

interface EnrichedLinks {
  bandcamp?: string;
  spotify?: string;
  youtube?: string;
  apple?: string;
  tidal?: string;
  amazon?: string;
  soundcloud?: string;
  website?: string;
  discogs?: string;
  deezer?: string;
  official?: string;
  instagram?: string;
  twitter?: string;
  facebook?: string;
}

interface ArtistMember {
  name: string;
  gid?: string;
}

interface ArtistCardProps {
  artist: {
    name: string;
    genres: string[];
    era: string | null;
    location: string | null;
    matchReason: string;
    confidence: number;
    tracks: string[];
    // Legacy links format
    links?: {
      bandcamp?: string;
      spotify?: string;
      youtube?: string;
    };
    // Pre-resolved embed data (from discovery or enrichment)
    youtubeVideoId?: string;
    bandcampEmbed?: BandcampEmbedData;
    // New: Full enriched data (from embed-resolver)
    enrichedData?: {
      links?: EnrichedLinks;
      members?: ArtistMember[];
      youtubeEmbed?: {
        videoId: string;
        title?: string;
      };
      bandcampEmbed?: BandcampEmbedData;
    };
  };
  getPlatformLinks: (name: string) => {
    bandcamp: string;
    spotify: string;
    apple: string;
    tidal: string;
    amazon: string;
  };
  /** Variant: 'full' shows header/metadata, 'embed-only' shows just the embed */
  variant?: 'full' | 'embed-only';
  /** Optional callback when an artist name is clicked (e.g., for in-lightbox navigation) */
  onArtistClick?: (name: string) => void;
  /** Hide the header (name already shown elsewhere) */
  hideHeader?: boolean;
}

export function ArtistCard({ artist, getPlatformLinks, variant = 'full', onArtistClick, hideHeader }: ArtistCardProps) {
  const [videoReady, setVideoReady] = useState(false);
  const isEmbedOnly = variant === 'embed-only';

  // ALWAYS fetch from API - this is the single source of truth for artist data
  // The API uses resolveArtistEmbeds() which is cached and versioned (EMBED_VERSION)
  // Stale versions trigger cache rebuild automatically
  // Pass ALL tracks - API will try each in the channel search before falling back
  const { data: artistData } = useArtistData(artist.name, artist.tracks);

  // Resolve embeds: API response is source of truth, prop data is fallback while loading
  // Note: artistData from useArtistData has youtubeVideoId (flat), not youtubeEmbed.videoId
  //
  // IMPORTANT: If API explicitly rejected (LLM said "none match"), don't fall back to props!
  // This prevents showing cover albums (e.g., "Instrumental Covers of Mandy Moore")
  const youtubeVideoId = artistData
    ? (artistData._meta?.youtubeRejected ? undefined : artistData.youtubeVideoId) // API responded - respect rejection
    : (artist.enrichedData?.youtubeEmbed?.videoId || artist.youtubeVideoId); // Still loading - use prop fallback
  const bandcampEmbed = artistData
    ? (artistData._meta?.bandcampRejected ? undefined : artistData.bandcampEmbed) // API responded - respect rejection
    : (artist.enrichedData?.bandcampEmbed || artist.bandcampEmbed); // Still loading - use prop fallback

  // DEBUG
  if (artist.name === 'Mandy Moore' || artist.name === 'Hoku') {
    console.log(`[ArtistCard] ${artist.name}:`,
      'apiData:', !!artistData,
      'bcRejected:', artistData?._meta?.bandcampRejected,
      'finalBC:', bandcampEmbed,
      'finalYT:', youtubeVideoId
    );
  }

  // Default to Bandcamp when available (support artists directly!)
  // Use state but sync with bandcampEmbed changes (e.g., when API overrides props)
  const [showBandcamp, setShowBandcamp] = useState(!!bandcampEmbed);

  // Sync showBandcamp when bandcampEmbed availability changes
  useEffect(() => {
    // If bandcamp becomes unavailable (e.g., API rejected it), switch to YouTube
    if (!bandcampEmbed && showBandcamp) {
      setShowBandcamp(false);
    }
    // If bandcamp becomes available and we have no YouTube, show bandcamp
    if (bandcampEmbed && !youtubeVideoId) {
      setShowBandcamp(true);
    }
  }, [bandcampEmbed, youtubeVideoId, showBandcamp]);

  const searchLinks = getPlatformLinks(artist.name);

  // Determine which embeds are available (pre-resolved)
  const hasYouTube = !!youtubeVideoId;
  const hasBandcamp = !!bandcampEmbed;
  const hasBoth = hasYouTube && hasBandcamp;
  // Will we show ANY embed? (includes fallback YouTube search when no pre-resolved data)
  const willShowEmbed = artist.tracks.length > 0;
  // What will we actually show? YouTube if pre-resolved OR as fallback when nothing pre-resolved
  const willShowYouTube = hasYouTube || (!hasYouTube && !hasBandcamp && willShowEmbed);

  // Links and members: API response is the COMPLETE source of truth
  // When artistData exists, use ONLY API data (with generic search fallbacks)
  // When artistData is undefined (still loading), use prop data as temporary fallback
  //
  // IMPORTANT: Don't mix API + props! When EMBED_VERSION changes, the entire
  // artist cache is rebuilt. Props contain stale data from before the rebuild.
  // Cast to Record to access all link types (MusicBrainzLinks is strict, but API may have more)
  const apiLinks = (artistData?.links || {}) as Record<string, string | undefined>;
  const links: AnyLinks = artistData
    ? {
        // API responded - use ONLY API links (search fallbacks for generic platforms only)
        bandcamp: apiLinks.bandcamp || bandcampEmbed?.bandcampUrl,
        spotify: apiLinks.spotify || searchLinks.spotify,
        youtube: apiLinks.youtube,
        apple: apiLinks.apple || searchLinks.apple,
        tidal: apiLinks.tidal || searchLinks.tidal,
        amazon: searchLinks.amazon, // MusicBrainz doesn't track Amazon
        soundcloud: apiLinks.soundcloud,
        discogs: apiLinks.discogs,
        deezer: apiLinks.deezer,
        official: apiLinks.official,
        instagram: apiLinks.instagram,
        twitter: apiLinks.twitter,
        facebook: apiLinks.facebook,
      }
    : {
        // Still loading - use prop data as temporary fallback
        bandcamp: artist.enrichedData?.links?.bandcamp || artist.links?.bandcamp || bandcampEmbed?.bandcampUrl || searchLinks.bandcamp,
        spotify: artist.enrichedData?.links?.spotify || artist.links?.spotify || searchLinks.spotify,
        youtube: artist.enrichedData?.links?.youtube || artist.links?.youtube,
        apple: artist.enrichedData?.links?.apple || searchLinks.apple,
        tidal: artist.enrichedData?.links?.tidal || searchLinks.tidal,
        amazon: searchLinks.amazon,
        soundcloud: artist.enrichedData?.links?.soundcloud,
        discogs: artist.enrichedData?.links?.discogs,
        deezer: artist.enrichedData?.links?.deezer,
        official: artist.enrichedData?.links?.official,
        instagram: artist.enrichedData?.links?.instagram,
        twitter: artist.enrichedData?.links?.twitter,
        facebook: artist.enrichedData?.links?.facebook,
      };

  // Members/memberOf: same pattern - API is complete truth when available
  const members = artistData ? artistData.members : artist.enrichedData?.members;
  const memberOf = artistData?.memberOf;

  // If no tracks to embed, mark as ready immediately so card details show
  // Also set a fallback timeout to always show details after 3 seconds
  useEffect(() => {
    if (artist.tracks.length === 0) {
      setVideoReady(true);
      return;
    }
    // Fallback: always show card details after 3 seconds even if embeds fail
    const timeout = setTimeout(() => {
      setVideoReady(true);
    }, 3000);
    return () => clearTimeout(timeout);
  }, [artist.tracks.length]);

  return (
    <div className={`artist-card ${videoReady ? 'ready' : 'loading'} ${isEmbedOnly ? 'embed-only' : ''}`}>
      {/* Header - hidden in embed-only mode or when hideHeader is true */}
      {!isEmbedOnly && !hideHeader && (
        <div className="artist-header">
          <h4 className="artist-name">{artist.name}</h4>
          {artist.location && <span className="location-badge">{artist.location}</span>}
        </div>
      )}

      {/* Toggle row - always render when we'll show an embed, for alignment */}
      {willShowEmbed && (
        <div className={`embed-toggle ${!hasBoth ? 'single-platform' : ''}`}>
          {hasBoth ? (
            <>
              <button
                className={`toggle-btn ${!showBandcamp ? 'active' : ''}`}
                onClick={() => setShowBandcamp(false)}
                title="YouTube"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </button>
              <button
                className={`toggle-btn ${showBandcamp ? 'active' : ''}`}
                onClick={() => setShowBandcamp(true)}
                title="Bandcamp"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M0 18.75l7.437-13.5H24l-7.438 13.5H0z"/>
                </svg>
              </button>
            </>
          ) : (
            /* Single platform - just show icon for alignment (YouTube if pre-resolved or fallback, else Bandcamp) */
            <span className="toggle-btn active" title={willShowYouTube ? 'YouTube' : 'Bandcamp'}>
              {willShowYouTube ? (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M0 18.75l7.437-13.5H24l-7.438 13.5H0z"/>
                </svg>
              )}
            </span>
          )}
        </div>
      )}

      {/* Embed area */}
      {artist.tracks.length > 0 && (
        <div className="embed-container">
          {/* Show YouTube when: has YouTube AND (not showing bandcamp OR doesn't have bandcamp) */}
          {hasYouTube && !showBandcamp && (
            <YouTubeEmbed
              query={`${artist.name} ${artist.tracks[0]}`}
              artistName={artist.name}
              trackName={artist.tracks[0]}
              onReady={() => setVideoReady(true)}
              tryBandcamp={false}
              preloadedVideoId={youtubeVideoId}
            />
          )}

          {/* Show Bandcamp when: has Bandcamp AND (showing bandcamp OR doesn't have YouTube) */}
          {hasBandcamp && (showBandcamp || !hasYouTube) && (
            <BandcampEmbed
              artistName={artist.name}
              trackName={artist.tracks[0]}
              preloadedEmbed={bandcampEmbed}
              onReady={() => setVideoReady(true)}
              size="medium"
            />
          )}

          {/* Fallback: no pre-resolved embeds, try YouTube search */}
          {!hasYouTube && !hasBandcamp && (
            <YouTubeEmbed
              query={`${artist.name} ${artist.tracks[0]}`}
              artistName={artist.name}
              trackName={artist.tracks[0]}
              onReady={() => setVideoReady(true)}
              tryBandcamp={true}
            />
          )}
        </div>
      )}

      {/* Details - hidden in embed-only mode */}
      {!isEmbedOnly && (
        <div className="card-details">
          {artist.matchReason && <p className="match-reason">{artist.matchReason}</p>}

          <div className="artist-meta">
            {artist.genres.slice(0, 3).map((g, j) => (
              <span key={j} className="genre-pill">{g}</span>
            ))}
            {artist.era && <span className="era-pill">{artist.era}</span>}
          </div>

          {artist.tracks.length > 0 && (
            <div className="key-tracks">
              <span className="tracks-label">Key tracks:</span>
              {artist.tracks.slice(0, 2).map((track, j) => (
                <a
                  key={j}
                  className="track-link"
                  href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`"${artist.name}" "${track}" official`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {track}
                </a>
              ))}
            </div>
          )}

          <PlatformLinks
            links={links}
            artistName={artist.name}
            size="medium"
            showSearchFallbacks={true}
          />

          {members && members.length > 0 && (
            <div className="artist-members">
              <span className="members-label">Members:</span>
              {members.slice(0, 5).map((m, i) => (
                m.name ? (
                  onArtistClick ? (
                    <button
                      key={i}
                      className="clickable-artist-name member-name"
                      onClick={() => onArtistClick(m.name)}
                    >
                      {m.name}
                    </button>
                  ) : (
                    <ClickableArtistName key={i} name={m.name} className="member-name" />
                  )
                ) : null
              ))}
            </div>
          )}

          {memberOf && memberOf.length > 0 && (
            <div className="artist-member-of">
              <span className="members-label">Also in:</span>
              {memberOf.slice(0, 5).map((bandName, i) => (
                onArtistClick ? (
                  <button
                    key={i}
                    className="clickable-artist-name member-name"
                    onClick={() => onArtistClick(bandName)}
                  >
                    {bandName}
                  </button>
                ) : (
                  <ClickableArtistName key={i} name={bandName} className="member-name" />
                )
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
